import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plug, Unplug, Monitor } from "lucide-react";
import {
  useGetConnectionsQuery,
  useRevokeConnectionMutation,
  type IntegrationConnection,
} from "@/redux/api/integrations.rtkq";
import { toast } from "sonner";

/**
 * Protected /integrations page — the office's connected external apps
 * (Legalix Connect). Pairing always STARTS inside the partner software;
 * here the office manages and revokes existing connections.
 */

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" }) : "—";

const ConnectionRow = ({ connection }: { connection: IntegrationConnection }) => {
  const [confirming, setConfirming] = useState(false);
  const [revokeConnection, { isLoading: revoking }] = useRevokeConnectionMutation();

  const handleRevoke = async () => {
    try {
      await revokeConnection(connection.id).unwrap();
      toast.success("החיבור נותק");
    } catch (err) {
      const data = (err as { data?: { message?: string } })?.data;
      toast.error(data?.message || "שגיאה בניתוק החיבור");
    } finally {
      setConfirming(false);
    }
  };

  const revoked = connection.status === "revoked";

  return (
    <div className="flex flex-col items-stretch justify-between gap-4 rounded-lg border p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
          <Plug className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{connection.partnerName}</span>
            {revoked ? (
              <Badge variant="outline" className="text-red-500 border-red-200">מנותק</Badge>
            ) : (
              <Badge variant="outline" className="text-green-600 border-green-200">פעיל</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {connection.pairedUserName || connection.pairedUserEmail}
            {connection.deviceName && (
              <span className="inline-flex items-center gap-1 mr-2">
                <Monitor className="h-3 w-3 inline" />
                {connection.deviceName}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground" dir="ltr">
            {connection.tokenPrefix}… · שימוש אחרון: {formatDate(connection.lastUsedAt)}
          </div>
        </div>
      </div>

      {!revoked &&
        (confirming ? (
          <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
            <Button size="sm" variant="destructive" className="flex-1 sm:flex-none" onClick={handleRevoke} disabled={revoking}>
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : "ניתוק סופי"}
            </Button>
            <Button size="sm" variant="ghost" className="flex-1 sm:flex-none" onClick={() => setConfirming(false)} disabled={revoking}>
              ביטול
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="w-full shrink-0 sm:w-auto" onClick={() => setConfirming(true)}>
            <Unplug className="ml-1 h-4 w-4" />
            ניתוק
          </Button>
        ))}
    </div>
  );
};

const IntegrationsPage = () => {
  const { data, isLoading, error } = useGetConnectionsQuery();

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6" dir="rtl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            אינטגרציות
          </CardTitle>
          <CardDescription>
            תוכנות חיצוניות המחוברות למשרד — חיבור חדש מתחילים מתוך התוכנה החיצונית
            (למשל: הגדרות ← חיבור ל־Legalix בעודכנית)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              שגיאה בטעינת החיבורים — נסו לרענן
            </p>
          ) : !data?.connections.length ? (
            <div className="text-center py-12">
              <Plug className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                אין עדיין חיבורים. החיבור מתחיל מתוך התוכנה החיצונית — לאחר האישור
                הוא יופיע כאן.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.connections.map((c) => (
                <ConnectionRow key={c.id} connection={c} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsPage;
